import { useContext, useState } from "react";
import { getItemDisplayString } from "features/configuration/GeneralConfiguration/utils";
import Button from "common/Buttons/Button";
import {
  ConfigurationItem,
  useConfigurationItem,
} from "features/configuration/GeneralConfiguration/ConfigurationItem";
import {
  GeneralConfigurationContext,
  ModalItemContext,
} from "features/configuration/GeneralConfiguration/generalConfigurationContexts";
import ConfigurationItemRow from "features/configuration/GeneralConfiguration/ConfigurationItem/ConfigurationItemRow";
import { zIndexes } from "@shared/common/Themes/AppTheme";
import Modal from "common/Modal/Modal";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { OrderTypes } from "@shared/features/trades/common/types.js";
import { LanguageNameSpaces } from "@shared/types/index.js";

const DISPLAY_CUTOFF = 100;

const ModalItem = ({ item, ...props }) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { itemName, overrideComponent, itemValueModified } =
    useConfigurationItem({ item });
  const { idToItem, editable } = useContext(GeneralConfigurationContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [okButton, setOkButton] = useState(null);
  const [extraActionButton, setExtraActionButton] = useState(null);
  const [cancelButton, setCancelButton] = useState(null);
  const [previousButton, setPreviousButton] = useState(null);
  const isCustomStrategies = props.onlyCustomStrategies;
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const handleCancel = () => {
    props.closeModal?.();
    setModalOpen(false);
  };

  const footer = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {previousButton || <span />}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {cancelButton}
        {extraActionButton}
        {okButton || (
          <Button type="primary" onClick={handleCancel}>
            OK
          </Button>
        )}
      </div>
    </div>
  );

  const display = getItemDisplayString(item, idToItem, false, true) || "";

  const isModalOpen = props.isModalVisible ?? modalOpen;

  const getModalTitle = () => {
    if (isCustomStrategies) {
      return t(ORDER_TYPE_TO_TITLE_DISPLAY[props.orderType]);
    }
    return itemName;
  };

  return (
    <>
      {!props.onlyCustomStrategies && (
        <ConfigurationItemRow
          nameComponent={itemName}
          extraInfoComponent={
            display.substr(0, DISPLAY_CUTOFF) +
            (display.length > DISPLAY_CUTOFF ? "..." : "")
          }
          overrideComponent={overrideComponent || <span />}
          onClickRow={editable ? () => setModalOpen(true) : null}
          itemValueModified={itemValueModified}
        />
      )}
      {isModalOpen && (
        <Modal
          onCancel={handleCancel}
          open={true}
          title={getModalTitle()}
          styles={{
            body: {
              position: "relative",
              minHeight: isCustomStrategies ? "auto" : "50vh",
              maxHeight: "70vh",
              overflow: "auto",
              padding: isMobile ? 16 : 24,
            },
          }}
          footer={footer}
          zIndex={zIndexes.modal}
        >
          <ModalItemContext.Provider
            value={{
              inModal: true,
              closeModal: handleCancel,
              setPreviousButton,
              setOkButton,
              setCancelButton,
              setExtraActionButton,
            }}
          >
            <ConfigurationItem item={item} {...props} />
          </ModalItemContext.Provider>
        </Modal>
      )}
    </>
  );
};

export default ModalItem;

export const ORDER_TYPE_TO_TITLE_DISPLAY = {
  [OrderTypes.ENTRY]: "custom_strategy.modal_entry_title",
  [OrderTypes.TAKE_PROFIT]: "custom_strategy.modal_take_profit_title",
  [OrderTypes.STOP]: "custom_strategy.modal_stop_title",
};
